import { ITeleconsultation } from "@/api_framework/api_modals/Users";
import { useTeleconsultationAPI } from "@/app/hooks/api_hooks/customer_profile/useTeleconsultationAPI";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Fragment, useEffect, useRef } from "react";
import toast from "react-hot-toast";

import { useClinicManagerPhoneAPI } from "@/app/hooks/api_hooks/customer_profile/useClinicManagerPhoneAPI";
import { useCustomerCallStatusAPI } from "@/app/hooks/api_hooks/customer_profile/useCustomerCallStatusAPI";
import { useCustomerServiceCallConnectAPI } from "@/app/hooks/api_hooks/customer_profile/useCustomerServiceCallConnectAPI";
import RBACFlag from "@/app/components/core/RBACFlag";
import { useRBACFlag } from "@/app/hooks/useRBACFlag";
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import { doc, onSnapshot } from "firebase/firestore";

import config from "@/configs/development";
import { useUtils } from "@/app/hooks/useUtils";
firebase.initializeApp(config.firebase);
const firestore = firebase.firestore();

export default function Teleconsultation({ setOpen, selected, customerId }) {
  const { execute: connectToTeleconsultation } = useTeleconsultationAPI();
  const { maskMobile } = useUtils();

  const { execute: checkCallButtonStatus, callButtonStatus } = useCustomerCallStatusAPI();

  const fetchClinicmanagersMobile = useClinicManagerPhoneAPI();
  const { getRouteKey } = useRouter();
  const { routeState } = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id"));
  const { clinicManagersMobile, customerDetails } = routeState.state as ICustomerProfileState;
  const user = useAppSelector((state) => state.auth.user);
  const userLoginInfo = useAppSelector((state) => state.auth.userLoginInfo);
  const { execute: connectServiceCall, isLoading: isServiceCallConnecting } = useCustomerServiceCallConnectAPI();
  const { status: canViewServiceCall } = useRBACFlag("SERVICE_CALL");

  const normalizePhoneNumber = (value?: string | number | null) => {
    if (!value) return "";
    const trimmed = String(value).trim();
    if (!trimmed) return "";
    if (trimmed.startsWith("+")) return trimmed;
    const digitsOnly = trimmed.replace(/\D/g, "");
    if (digitsOnly.length === 10) return `+91${digitsOnly}`;
    if (digitsOnly.length > 0) return `+${digitsOnly}`;
    return "";
  };

  const getFromNumber = () => normalizePhoneNumber(user?.mobile ?? userLoginInfo?.mobile ?? null);
  const getToNumber = () => normalizePhoneNumber(selected || customerDetails?.mobile);
  const fromNumber = getFromNumber();
  const toNumber = getToNumber();
  const isServiceCallEnabled = Boolean(fromNumber && toNumber);
  const isServiceCallVisible = canViewServiceCall;

  const handleClose = () => {
    setOpen(false);
  };
  useEffect(() => {
    fetchClinicmanagersMobile();
  }, []);

  useEffect(() => {
    if (customerId) {
      checkCallButtonStatus(customerId, customerDetails?.mobile || selected);
    }
  }, [customerId]);

  const cancelButtonRef = useRef(null);

  const fetchTeleconsultationCallStatus = () => {
    try {
      const statusRef = doc(firestore, "my_operator", user?.email, "call_data", customerId);
      const unsubscribe = onSnapshot(statusRef, (snapshot) => {
        if (snapshot.exists()) {
          const callstatus = snapshot.data();
          if (callstatus.status === "success") {
            toast.success(`${callstatus.text}`);
            unsubscribe();
          } else if (callstatus.is_accepted) {
            // Update your UI accordingly
          } else if (callstatus.is_failed) {
            //todo
          } else if (callstatus.status === "error") {
            toast.error(`${callstatus.text}`);
            unsubscribe();
          }
        }
      });
      // Return the unsubscribe function to stop listening when necessary
    } catch (error) {
      // Do Something
    }
  };

  const handelCallToClient = (callSource: string) => {
    const mobile1 = clinicManagersMobile?.fields?.phone1;
    const mobile2 = clinicManagersMobile?.fields?.phone2;
    const mobile = [];
    if (mobile1) {
      mobile.push(mobile1);
    }
    if (mobile2) {
      mobile.push(mobile2);
    }

    const constructedBody: ITeleconsultation = {
      mobiles: mobile,

      unique_id: customerId,
      call_type: callSource,
      dashboard_user_email: user?.email,
    };
    connectToTeleconsultation(constructedBody).then(async () => {
      await fetchTeleconsultationCallStatus();
      await setOpen(false);
    });
  };

  const handleServiceCall = async () => {
    if (!isServiceCallEnabled || isServiceCallConnecting) return;
    await connectServiceCall({
      from_number: getFromNumber(),
      to_number: getToNumber(),
      record: true,
      status_callback_content_type: "application/json",
      status_callback_events: ["terminal", "answered"],
    });
  };
  return (
    <div>
      <Transition.Root show={true} as={Fragment}>
        <Dialog as="div" className="relative z-40" initialFocus={cancelButtonRef} onClose={handleClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/50 transition-opacity" />
          </Transition.Child>

          <div className="z-10 fixed inset-0 w-screen overflow-y-auto">
            <div className="flex justify-center items-end sm:items-center p-4 min-h-full text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative flex flex-col bg-white shadow-xl rounded-[12px] w-full max-w-[520px] max-h-[90vh] overflow-hidden text-left transition-all">
                  <div className="flex justify-between items-center px-6 py-5 border-[#EEE] border-b">
                    <div>
                      <h3 className="m-0 font-semibold text-black text-base">Communication</h3>
                      <p className="mt-1 mb-0 text-[#666] text-xs">Call {maskMobile(selected)}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setOpen(false)}
                      className="flex justify-center items-center bg-[#808DA01A] p-0 border-none rounded-full w-7 h-7 cursor-pointer"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="flex-1 space-y-4 px-6 py-5 overflow-y-auto">
                    <div className="flex justify-between items-center">
                      <h4 className="font-medium text-[#666] text-xs">Contact Number</h4>
                      <p className="text-black text-sm">{maskMobile(selected)}</p>
                    </div>

                    <div>
                      <h4 className="mb-2 font-medium text-[#666] text-xs">Call</h4>
                      <div className="flex flex-wrap items-center gap-3">
                        {callButtonStatus?.is_call_button_visible && (
                          <button
                            className="flex items-center gap-1 bg-[#193A6A] px-3 py-1.5 rounded-md font-medium text-white text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={handleServiceCall}
                            disabled={!isServiceCallEnabled || isServiceCallConnecting}
                          >
                            <img
                              src="https://vetic-img.s3.ap-south-1.amazonaws.com/website/admin-tool/callIcon.svg"
                              alt="call"
                              className="w-3 h-3"
                            />
                            <span>{isServiceCallConnecting ? "Calling..." : "Client Call"}</span>
                          </button>
                        )}
                        {callButtonStatus?.is_call_button_visible_teleconsultation && (
                          <button
                            className="flex items-center gap-1 bg-[#193A6A] px-3 py-1.5 rounded-md font-medium text-white text-xs"
                            onClick={() => handelCallToClient("doctor")}
                          >
                            <img
                              src="https://vetic-img.s3.ap-south-1.amazonaws.com/website/admin-tool/docsupportIcon.svg"
                              alt="call"
                              className="w-3 h-3"
                            />
                            <span>Teleconsultation Call</span>
                          </button>
                        )}
                        <RBACFlag actionId="SERVICE_CALL">
                          <button
                            disabled={!isServiceCallEnabled || isServiceCallConnecting}
                            className="flex items-center gap-1 bg-[#193A6A] px-3 py-1.5 rounded-md font-medium text-white text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={handleServiceCall}
                          >
                            <img
                              src="https://vetic-img.s3.ap-south-1.amazonaws.com/website/admin-tool/callIcon.svg"
                              alt="call"
                              className="w-3 h-3"
                            />
                            <span>{isServiceCallConnecting ? "Calling..." : "Service Call"}</span>
                          </button>
                        </RBACFlag>
                        {!callButtonStatus?.is_call_button_visible &&
                          !callButtonStatus?.is_call_button_visible_teleconsultation &&
                          !isServiceCallVisible && (
                            <p className="text-[#666] text-xs">No active teleconsultation available for this user</p>
                          )}
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 bg-[#FAFAFA] px-6 py-4 border-[#EEE] border-t">
                    <button
                      type="button"
                      ref={cancelButtonRef}
                      onClick={() => setOpen(false)}
                      className="flex justify-center items-center gap-1.5 bg-white hover:bg-[#F4F4F4] px-5 border border-[#D9DEE7] rounded-[8px] h-10 font-semibold text-[#111111] text-[13px] cursor-pointer"
                    >
                      Close
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
}
